// CORE INTELLIGENCE's view model and state (#6779, epic #6552; prototype `:3685-3841`): the
// squads, their agents by role, the gate counts and the newest merges off `GET
// /v1/fleet/live` (or its `fleet_live` frames, folded by `mergeFleetLive`), plus the pure
// pieces the canvas needs: the dot budget, the cluster geometry, the beams, the eye and the
// squad focus. Pure: nothing here touches the DOM or a clock it was not handed.
//
// OMITTED, NEVER SIMULATED (owner ruling V7):
//   - a squad with no `agents.by_squad` key has `agents: null` (no dots, no count, no bars);
//   - a squad with no `at_gate.by_squad` key has `atGate: null`, one with no
//     `last_merge_by_squad` key has `lastMerge: null`; an absent `commits` is `null`;
//   - there is no "Idle" role: the wire has no source for an idle agent (brief.md item 71);
//   - one dot per agent of a shown squad, never padded (the prototype padded to 900,
//     `:3701`); past {@link MAX_DOTS} a dot stands for several agents, and every count
//     stays the wire's.

import type { FleetLiveStage, FleetLiveWire } from '@developerz/domain';
import { idHash } from './fleet-live';

type EventWire = FleetLiveWire['events'][number];
type ItemWire = FleetLiveWire['items'][number];

/** The most squads the read serves and the Core shows (#7010 D5, ruling G-9). */
export const MAX_SQUADS = 8;
/** The most dots drawn; above it each dot stands for several agents. */
export const MAX_DOTS = 1_500;
/** A beam's life, and the most alive at once. */
export const BEAM_LIFE_MS = 2_600;
export const MAX_BEAMS = 24;
/** The squad focus cycles this often, and a person's choice holds it this long. */
export const CYCLE_MS = 8_000;
export const HOLD_MS = 20_000;
/** A click this close to a cluster's centre selects it. */
export const PICK_RADIUS_PX = 70;
/** The newest in-flight items "Live in this squad" lists. */
export const LIVE_LINES = 3;

export const CORE_ROLES = ['writing', 'reviewing', 'fixing'] as const;
export type CoreRole = (typeof CORE_ROLES)[number];
export type CoreBuckets = Readonly<Record<CoreRole, number>>;

/** The stages `counts.in_flight` counts (`fleet-live-assemble.ts`). */
const IN_FLIGHT: ReadonlySet<FleetLiveStage> = new Set<FleetLiveStage>([
  'Picked up',
  'Pull request',
  'Fixed until green',
]);

export type CoreLiveLine = {
  readonly id: string;
  readonly number: number | null;
  readonly title: string;
};

export type CoreSquad = {
  readonly key: string;
  readonly name: string;
  /** Repo full names, sorted by name: the first is the Merge Gate's default subject. */
  readonly repos: readonly string[];
  /** The squad's agents by role, or `null` when the summary sent no bucket for it. */
  readonly agents: CoreBuckets | null;
  /** The bucket sum, or `null` with `agents`. */
  readonly total: number | null;
  readonly atGate: number | null;
  readonly lastMerge: { readonly number: number; readonly at: string } | null;
  readonly live: readonly CoreLiveLine[];
};

export type CoreModel = {
  /** `agents.total`: every live agent, so it can exceed the drawn sum. */
  readonly total: number;
  readonly squads: readonly CoreSquad[];
  /** Every squad, the shown ones plus `squads_more`. */
  readonly squadCount: number;
  readonly more: number;
  readonly commitsPerMinute: number | null;
  /** Open PRs at the gate over every squad (`at_gate.total`). */
  readonly atGateTotal: number;
  /** The agents of the shown squads' buckets: the ones that get a dot. */
  readonly drawn: number;
  /** `total` less `drawn`, never negative: agents with no shown squad. */
  readonly unshown: number;
};

const byName = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** Newest `since` first, ties by `id` descending (the API's tie direction). */
function newestFirst(a: ItemWire, b: ItemWire): number {
  if (a.since !== b.since) return a.since < b.since ? 1 : -1;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

function liveOf(items: readonly ItemWire[], key: string): CoreLiveLine[] {
  return items
    .filter((i) => i.squad_key === key && IN_FLIGHT.has(i.stage))
    .sort(newestFirst)
    .slice(0, LIVE_LINES)
    .map((i) => ({ id: i.id, number: i.number ?? null, title: i.title }));
}

/** The Core's whole model, off one wire body. */
export function coreModel(wire: FleetLiveWire): CoreModel {
  const squads = wire.squads.slice(0, MAX_SQUADS).map((s): CoreSquad => {
    const b = wire.agents.by_squad[s.key];
    const agents =
      b === undefined ? null : { writing: b.writing, reviewing: b.reviewing, fixing: b.fixing };
    const merge = wire.last_merge_by_squad[s.key];
    return {
      key: s.key,
      name: s.name,
      repos: s.repos.map((r) => r.full_name).sort(byName),
      agents,
      total: agents === null ? null : agents.writing + agents.reviewing + agents.fixing,
      atGate: wire.at_gate.by_squad[s.key] ?? null,
      lastMerge: merge === undefined ? null : { number: merge.number, at: merge.at },
      live: liveOf(wire.items, s.key),
    };
  });
  const drawn = squads.reduce((n, s) => n + (s.total ?? 0), 0);
  return {
    total: wire.agents.total,
    squads,
    squadCount: wire.squads.length + wire.squads_more,
    more: wire.squads_more,
    commitsPerMinute: wire.commits?.per_minute ?? null,
    atGateTotal: wire.at_gate.total,
    drawn,
    unshown: Math.max(0, wire.agents.total - drawn),
  };
}

/** One role bar of the squad feed: its count and its width (a 1.5% floor). */
export type RoleBar = { readonly role: CoreRole; readonly count: number; readonly pct: number };

/** The squad's three role bars, or `null` when it has no bucket (no bars, never zeros). */
export function roleBars(squad: CoreSquad): RoleBar[] | null {
  const a = squad.agents;
  if (a === null) return null;
  const max = Math.max(1, squad.total ?? 0);
  return CORE_ROLES.map((role) => ({
    role,
    count: a[role],
    pct: Math.max(1.5, (a[role] / max) * 100),
  }));
}

// ─── Geometry ─────────────────────────────────────────────────────────────────

export type Vec3 = { readonly x: number; readonly y: number; readonly z: number };

/** A deterministic [0, 1) from a seed, so a dot keeps its place across patches. */
export function seeded(seed: number): number {
  let h = (seed ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4_294_967_296;
}

/** `n` squad directions spread over a sphere (the prototype's Fibonacci lattice). */
export function squadDirections(n: number): Vec3[] {
  return Array.from({ length: n }, (_, i) => {
    const phi = Math.acos(1 - (2 * (i + 0.5)) / n);
    const th = Math.PI * (1 + Math.sqrt(5)) * i;
    return { x: Math.sin(phi) * Math.cos(th), y: Math.cos(phi), z: Math.sin(phi) * Math.sin(th) };
  });
}

export type CoreDot = Vec3 & {
  readonly squad: number;
  readonly role: CoreRole;
  /** Twinkle phase and speed. */
  readonly ph: number;
  readonly sp: number;
};

export type DotPlan = {
  readonly dots: readonly CoreDot[];
  /** Agents per dot: 1 up to {@link MAX_DOTS}, more above it. */
  readonly weight: number;
};

/** How many dots each (squad, role) gets: one per agent, or a share of the budget. */
function dotCounts(squads: readonly CoreSquad[], budget: number): number[][] {
  const raw = squads.map((s) => CORE_ROLES.map((r) => s.agents?.[r] ?? 0));
  const sum = raw.flat().reduce((a, b) => a + b, 0);
  if (sum <= budget) return raw;
  // Largest remainder, so the dots add up to the budget exactly.
  const share = raw.map((row) => row.map((n) => (n * budget) / sum));
  const out = share.map((row) => row.map(Math.floor));
  let left = budget - out.flat().reduce((a, b) => a + b, 0);
  const rest = share
    .flatMap((row, i) => row.map((v, j) => ({ i, j, r: v - Math.floor(v) })))
    .sort((a, b) => b.r - a.r);
  for (const c of rest) {
    if (left <= 0) break;
    const row = out[c.i];
    if (row !== undefined) row[c.j] = (row[c.j] ?? 0) + 1;
    left -= 1;
  }
  return out;
}

/** The dots to draw, each in its squad's cluster, by role. Deterministic per squad key. */
export function dotPlan(squads: readonly CoreSquad[], budget = MAX_DOTS): DotPlan {
  const sum = squads.reduce((n, s) => n + (s.total ?? 0), 0);
  const counts = dotCounts(squads, budget);
  const dirs = squadDirections(squads.length);
  const dots: CoreDot[] = [];
  squads.forEach((s, si) => {
    const d = dirs[si] ?? { x: 0, y: 1, z: 0 };
    const base = idHash(s.key);
    CORE_ROLES.forEach((role, ri) => {
      const n = counts[si]?.[ri] ?? 0;
      for (let i = 0; i < n; i += 1) {
        const seed = base + ri * 100_003 + i * 7_919;
        const r = (k: number): number => seeded(seed + k);
        const jitter = (k: number): number => (r(k) + r(k + 1) + r(k + 2) - 1.5) * 0.55;
        const x = d.x + jitter(0);
        const y = d.y + jitter(3);
        const z = d.z + jitter(6);
        const l = Math.sqrt(x * x + y * y + z * z) || 1;
        const rad = 0.58 + r(9) * 0.44;
        dots.push({
          x: (x / l) * rad,
          y: (y / l) * rad,
          z: (z / l) * rad,
          squad: si,
          role,
          ph: r(10) * 6.28,
          sp: 0.6 + r(11) * 0.8,
        });
      }
    });
  });
  return { dots, weight: sum > budget ? sum / budget : 1 };
}

/** The Core canvas's height for a stage `w` wide. */
export function coreHeight(w: number): number {
  return Math.round(Math.max(330, Math.min(500, w * 0.62)));
}

export type Projected = {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly k: number;
};

/** A point turned by `rotY`, tilted by `tilt`, projected with perspective (prototype `proj`). */
export function project(
  p: Vec3,
  c: { readonly cx: number; readonly cy: number; readonly r: number; readonly sx: number },
  rotY: number,
  tilt: number,
): Projected {
  const cos = Math.cos(rotY);
  const sin = Math.sin(rotY);
  const x = p.x * cos - p.z * sin;
  const z = p.x * sin + p.z * cos;
  const ct = Math.cos(tilt);
  const st = Math.sin(tilt);
  const y2 = p.y * ct - z * st;
  const z2 = p.y * st + z * ct;
  const k = 1 / (1 + z2 * 0.32);
  return { x: c.cx + x * c.r * c.sx * k, y: c.cy + y2 * c.r * k, z: z2, k };
}

/** The index of the cluster centre nearest `(x, y)` within {@link PICK_RADIUS_PX}, else -1. */
export function nearestCluster(
  centres: readonly { readonly x: number; readonly y: number; readonly n: number }[],
  x: number,
  y: number,
): number {
  let best = -1;
  let bd = PICK_RADIUS_PX;
  centres.forEach((c, i) => {
    if (c.n === 0) return;
    const d = Math.hypot(c.x - x, c.y - y);
    if (d < bd) {
      bd = d;
      best = i;
    }
  });
  return best;
}

// ─── Beams ────────────────────────────────────────────────────────────────────

export type BeamTone = 'live' | 'shipped' | 'red';

export type Beam = {
  readonly squad: number;
  /** Which of the squad's dots it leaves from (the painter wraps it into range). */
  readonly seed: number;
  readonly born: number;
  readonly tone: BeamTone;
};

const BEAM_TONE: Readonly<Partial<Record<EventWire['kind'], BeamTone>>> = {
  pick: 'live',
  pr: 'live',
  merged: 'shipped',
  ci: 'red',
};

const eventKey = (e: EventWire): string => `${e.at}|${e.kind}|${e.item.id}`;

export type Beams = {
  /**
   * Note the events the stream delivered. The FIRST call marks every event seen and
   * fires nothing (no beam on first paint); after it, each event whose `(at, kind,
   * item.id)` was not in the previous `events[]` fires one beam, when its kind draws one
   * and its item names a shown squad. `moving: false` (reduced motion) fires none, and
   * still marks the events seen.
   */
  readonly note: (
    events: readonly EventWire[],
    items: readonly ItemWire[],
    squads: readonly CoreSquad[],
    now: number,
    moving: boolean,
  ) => void;
  /** The beams alive at `now`, at most {@link MAX_BEAMS}, newest last. */
  readonly live: (now: number) => readonly Beam[];
};

export function createBeams(): Beams {
  let seen: ReadonlySet<string> | null = null;
  let beams: Beam[] = [];
  return {
    note(events, items, squads, now, moving) {
      const prev = seen;
      seen = new Set(events.map(eventKey));
      if (prev === null || !moving) return;
      const squadOf = new Map(items.map((i) => [i.id, i.squad_key]));
      const index = new Map(squads.map((s, i) => [s.key, i]));
      for (const e of events) {
        const tone = BEAM_TONE[e.kind];
        if (tone === undefined || prev.has(eventKey(e))) continue;
        const key = squadOf.get(e.item.id);
        const squad = key === undefined ? undefined : index.get(key);
        if (squad === undefined) continue;
        beams.push({ squad, seed: idHash(e.item.id), born: now, tone });
      }
      beams = beams.slice(-MAX_BEAMS);
    },
    live(now) {
      beams = beams.filter((b) => now - b.born < BEAM_LIFE_MS);
      return beams;
    },
  };
}

// ─── Squad focus ──────────────────────────────────────────────────────────────

/**
 * Which squad the canvas and the squad feed show. `nextAt` is when the cycle next moves
 * (`null` before the first frame); `holdPending` marks a person's choice not yet stamped
 * with a frame time. The Merge Gate's subject is NOT here: the cycle never moves it.
 */
export type CoreFocus = {
  readonly index: number;
  readonly nextAt: number | null;
  readonly holdPending: boolean;
};

export const FIRST_FOCUS: CoreFocus = { index: 0, nextAt: null, holdPending: false };

/** The focus at frame time `now`: a held choice holds 20 s, else the cycle moves every 8 s. */
export function tickFocus(f: CoreFocus, now: number, count: number): CoreFocus {
  if (count === 0) return f.index === 0 && !f.holdPending ? f : FIRST_FOCUS;
  const index = f.index % count;
  if (f.holdPending) return { index, nextAt: now + HOLD_MS, holdPending: false };
  if (f.nextAt === null) return { index, nextAt: now + CYCLE_MS, holdPending: false };
  if (now < f.nextAt) return index === f.index ? f : { ...f, index };
  return { index: (index + 1) % count, nextAt: now + CYCLE_MS, holdPending: false };
}

/** A person chose squad `index` (a tab, a cluster or a repo name): hold it. */
export function holdFocus(f: CoreFocus, index: number): CoreFocus {
  return { index, nextAt: f.nextAt, holdPending: true };
}

// ─── The Merge Gate's subject ─────────────────────────────────────────────────

/** The one repo the Merge Gate acts on (the override is per repo, ruling V8). */
export type GateSubject = { readonly squad: string; readonly repo: string };

/** The first shown squad's first repo by name, or `null` when no squad has a repo. */
export function defaultSubject(squads: readonly CoreSquad[]): GateSubject | null {
  for (const s of squads) {
    const repo = s.repos[0];
    if (repo !== undefined) return { squad: s.key, repo };
  }
  return null;
}

/** `current`, while its repo is still in its squad; else the default. */
export function resolveSubject(
  current: GateSubject | null,
  squads: readonly CoreSquad[],
): GateSubject | null {
  if (current !== null) {
    const s = squads.find((x) => x.key === current.squad);
    if (s?.repos.includes(current.repo)) return current;
  }
  return defaultSubject(squads);
}

/** The subject a person's choice of squad `key` (and optionally one of its repos) makes. */
export function chooseSubject(
  squads: readonly CoreSquad[],
  key: string,
  repo?: string,
): GateSubject | null {
  const s = squads.find((x) => x.key === key);
  if (s === undefined) return null;
  const pick = repo !== undefined && s.repos.includes(repo) ? repo : s.repos[0];
  return pick === undefined ? null : { squad: key, repo: pick };
}
